import {
  ApiSimpleSubscription,
  ApiSubscriptionMerchant,
  ApiSubscriptionStatus,
} from "@incendium/api";
import {
  Alert,
  Autocomplete,
  Button,
  DrawerProps,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SavingButton from "Components/UI/SavingButton";
import StyledDrawer, {
  StyledDrawerActions,
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import useContracts from "features/subscription/hooks/useContracts";
import useSubscriptionProducts from "features/subscription/hooks/useSubscriptionProducts";
import produce from "immer";
import { CallbackOrVal } from "Interfaces";
import { useEffect, useMemo, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { usePrevious, useUpdateEffect } from "react-use";

interface ISubscriptionDrawerProps extends DrawerProps {
  onSubmit: () => void;
  subscription: ApiSimpleSubscription;
  setSubscription: (v: CallbackOrVal<ApiSimpleSubscription>) => void;
  saving: boolean;
}
function SubscriptionDrawer({
  open,
  onClose,
  onSubmit,
  subscription,
  setSubscription,
  saving,
}: ISubscriptionDrawerProps) {
  const { contracts, loading } = useContracts();
  const { subscriptionProducts, loading: pLoading } = useSubscriptionProducts();
  const [price, setPrice] = useState((subscription.price || 0) / 100);
  const [annualPrice, setAnnualPrice] = useState(
    (subscription.annualPrice || 0) / 100,
  );
  const prevOpen = usePrevious(open);

  useUpdateEffect(() => {
    setSubscription((sub) =>
      produce(sub, (draft) => {
        draft.price = Math.round(price * 100);
      }),
    );
  }, [price]);

  useUpdateEffect(() => {
    setSubscription((sub) =>
      produce(sub, (draft) => {
        draft.annualPrice = Math.round(annualPrice * 100);
      }),
    );
  }, [annualPrice]);

  useEffect(() => {
    if (!open && prevOpen) {
      setSubscription({});
      setPrice(0);
      setAnnualPrice(0);
    }
    if (!prevOpen && open) {
      setPrice((subscription.price || 0) / 100);
      setAnnualPrice((subscription.annualPrice || 0) / 100);
    }
  }, [open, prevOpen, subscription, setSubscription]);

  const selectedProduct = useMemo(() => {
    return (subscriptionProducts || []).find(
      (f) => f.id === subscription.productId,
    );
  }, [subscription.productId, subscriptionProducts]);

  const selectedContract = useMemo(() => {
    return (contracts || []).find((f) => f.id === subscription.contractId);
  }, [subscription.contractId, contracts]);

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <StyledDrawerTitle>
        <Typography variant="subtitle1">Subscription</Typography>
      </StyledDrawerTitle>
      <ValidatorForm onSubmit={onSubmit}>
        <StyledDrawerContainer sx={{ width: 600 }}>
          <Stack spacing={2}>
            <TextValidator
              label="Subscription Name"
              helperText="This is for internal use only."
              value={subscription.name || ""}
              variant="outlined"
              name="name"
              size="small"
              fullWidth
              validators={["required"]}
              errorMessages={["Name is required"]}
              onChange={(e: any) =>
                setSubscription(
                  produce(subscription, (draft) => {
                    draft.name = e.target.value;
                  }),
                )
              }
            />
            <TextValidator
              label="Subscription Description"
              helperText={
                <>
                  This will the sbscription description the user will see. This
                  could be something like <br />
                  10,000 pages
                  <br /> 10 sales
                  <br /> ...etc
                </>
              }
              value={subscription.description || ""}
              variant="outlined"
              name="description"
              size="small"
              fullWidth
              multiline
              rows={4}
              rowsMax={8}
              validators={["required"]}
              errorMessages={["Description is required"]}
              onChange={(e: any) =>
                setSubscription(
                  produce(subscription, (draft) => {
                    draft.description = e.target.value;
                  }),
                )
              }
            />
            <Typography variant="body2" color="text.secondary">
              Volume limits are optional, but subscriptions for the same
              merchant should use the same metric (orders, pageviews, or
              revenue). Use -1 for unlimited.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max Orders"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={subscription.maxOrders ?? ""}
                inputProps={{ min: -1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSubscription((sub) =>
                    produce(sub, (draft) => {
                      draft.maxOrders = raw === "" ? undefined : Number(raw);
                    }),
                  );
                }}
              />
              <TextField
                label="Max Pageviews"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={subscription.maxPageviews ?? ""}
                inputProps={{ min: -1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSubscription((sub) =>
                    produce(sub, (draft) => {
                      draft.maxPageviews = raw === "" ? undefined : Number(raw);
                    }),
                  );
                }}
              />
              <TextField
                label="Max Revenue"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={subscription.maxRevenue ?? ""}
                inputProps={{ min: -1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSubscription((sub) =>
                    produce(sub, (draft) => {
                      draft.maxRevenue = raw === "" ? undefined : Number(raw);
                    }),
                  );
                }}
              />
            </Stack>
            {subscription.status ===
            ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE ? (
              <>
                <Alert severity="info">
                  This subscription is active, you cannot adjust price.
                </Alert>
              </>
            ) : (
              <>
                <TextValidator
                  label="Subscription Monthly Price"
                  value={price}
                  type="number"
                  variant="outlined"
                  name="price"
                  size="small"
                  fullWidth
                  validators={["required"]}
                  errorMessages={["Price is required"]}
                  onChange={(e: any) => setPrice(e.target.value)}
                />
                <TextValidator
                  label="Subscription Yearly Price"
                  value={annualPrice}
                  type="number"
                  variant="outlined"
                  name="price"
                  size="small"
                  fullWidth
                  onChange={(e: any) => setAnnualPrice(e.target.value)}
                />
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Is this subscription for Stripe or Shopify?
                  </Typography>
                  <StyledFlexBox compact width={200}>
                    <Typography>Stripe</Typography>
                    <Switch
                      disabled={loading}
                      checked={
                        subscription.merchant ===
                        ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_SHOPIFY
                      }
                      onChange={(e, checked) =>
                        setSubscription((sub) =>
                          produce(sub, (draft) => {
                            draft.merchant = checked
                              ? ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_SHOPIFY
                              : ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_STRIPE;
                          }),
                        )
                      }
                    />
                    <Typography>Shopify</Typography>
                  </StyledFlexBox>
                </Stack>
              </>
            )}
            <Autocomplete
              id="product-picker"
              size="small"
              onChange={(e, value) => {
                setSubscription((sub) =>
                  produce(sub, (draft) => {
                    draft.productId = value?.id;
                  }),
                );
              }}
              disableClearable
              value={selectedProduct}
              isOptionEqualToValue={(option, value) => {
                return option?.id === value?.id;
              }}
              options={subscriptionProducts}
              loading={pLoading}
              getOptionLabel={(o) => o.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a product"
                  variant="outlined"
                />
              )}
            />
            <Autocomplete
              id="contract-picker"
              size="small"
              onChange={(e, value) => {
                setSubscription((sub) =>
                  produce(sub, (draft) => {
                    draft.contractId = value?.id;
                  }),
                );
              }}
              value={selectedContract}
              isOptionEqualToValue={(option, value) => {
                return option?.id === value?.id;
              }}
              options={contracts}
              loading={loading}
              getOptionLabel={(o) => o.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a contract"
                  variant="outlined"
                />
              )}
            />
          </Stack>
        </StyledDrawerContainer>
        <StyledDrawerActions>
          <Button
            onClick={(e) => onClose!(e, "backdropClick")}
            color="secondary"
          >
            Cancel
          </Button>
          <SavingButton
            variant="contained"
            color="primary"
            type="submit"
            saving={saving}
          >
            Save
          </SavingButton>
        </StyledDrawerActions>
      </ValidatorForm>
    </StyledDrawer>
  );
}

export default SubscriptionDrawer;
